import { useCallback, useEffect, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';

import {
  FACEBOOK_APP_ID,
  IG_LOGIN_APP_ID,
  IG_LOGIN_REDIRECT_URI,
  IG_LOGIN_SCOPES,
} from '../../config';
import { FB_VERSION, startFacebookLogin } from '../auth/facebookAccount';
import { CREATOR_SCOPES, GOOGLE_AUTH_OPTIONS } from '../auth/googleScopes';

/** Whether the direct Instagram route is configured for this build. */
export const instagramLoginAvailable = Boolean(IG_LOGIN_APP_ID);

/**
 * Send the creator to Instagram to authorise, directly.
 *
 * The Facebook route below requires them to own a Facebook Page with the
 * Instagram account linked to it, and turns away everyone who does not —
 * which is most creators. This asks Instagram itself for the same insights
 * permission with none of that.
 *
 * A full-page redirect rather than a popup: Instagram's authorise screen
 * refuses to render in one, and a popup that silently fails looks to the
 * creator like a broken button.
 */
export function startInstagramLogin({ state = '' } = {}) {
  const params = new URLSearchParams({
    client_id: IG_LOGIN_APP_ID,
    redirect_uri: IG_LOGIN_REDIRECT_URI,
    scope: IG_LOGIN_SCOPES.join(','),
    response_type: 'code',
  });
  if (state) params.set('state', state);
  window.location.href = `https://www.instagram.com/oauth/authorize?${params.toString()}`;
}

function loadFacebookSdk() {
  return new Promise((resolve) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = function fbAsyncInit() {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: FB_VERSION,
      });
      resolve(window.FB);
    };
    const existing = document.getElementById('facebook-jssdk');
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  });
}

/**
 * Returns [startLogin, ready]. `onSuccess` receives the Facebook authResponse,
 * which is what POST /users/facebook/registration expects, plus the
 * {page, instagramAccount, pages} resolved from /me/accounts.
 */
export function useInstagramLogin({ onSuccess, onError }) {
  const [ready, setReady] = useState(Boolean(window.FB));

  useEffect(() => {
    let active = true;
    loadFacebookSdk().then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  const startLogin = useCallback(() => {
    if (!window.FB) {
      if (onError) onError(new Error('Facebook sign-in is still loading.'));
      return;
    }

    startFacebookLogin(window.FB, { userType: 'influencer', onSuccess, onError });
  }, [onSuccess, onError]);

  return [startLogin, ready];
}

/** Returns startLogin; `onSuccess` receives {code, ...} for the backend. */
export function useYoutubeLogin({ onSuccess, onError }) {
  return useGoogleLogin({
    ...GOOGLE_AUTH_OPTIONS,
    scope: CREATOR_SCOPES.join(' '),
    onSuccess: (response) => onSuccess(response),
    onError: (error) => {
      if (onError) onError(error);
    },
  });
}
